import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { ClarityEvents, trackClarityEvent } from "../../clarity";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import {
  enabledQueryParameterValue,
  getPartnerAccessDeniedPath,
  partnerQueryParameters,
  xboxOnboardSiteLocationPrefix,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { useAutomaticInvitationEntry } from "../../helpers/useAutomaticInvitationEntry";
import { useStrings } from "../../localization/localizationHelpers";
import { xboxOnboardActions } from "../../redux/actions/xboxOnboardActions";
import {
  getXboxOnboardData,
  getXboxOnboardError,
  getXboxOnboardIsLoading,
  getXboxOnboardIsSubmitting,
} from "../../redux/selectors/xboxOnboardSelectors";
import { XboxOnboardApplicationStatus } from "../../server/crm/api/models/xboxOnboardModels";
import { XboxOnboardForm, XboxOnboardStatus } from "./components";
import * as styles from "./XboxOnboardPage.module.css";

type OnboardingLocationState = Readonly<{
  skipInvitation?: boolean;
}>;

const XboxOnboardPage = () => {
  const isSignedIn = useRequireAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<OnboardingLocationState>();
  const data = useSelector(getXboxOnboardData);
  const isLoading = useSelector(getXboxOnboardIsLoading);
  const isSubmitting = useSelector(getXboxOnboardIsSubmitting);
  const error = useSelector(getXboxOnboardError);
  const skipInvitation =
    new URLSearchParams(location.search).get(partnerQueryParameters.skipInvitation) ===
      enabledQueryParameterValue || location.state?.skipInvitation === true;
  const shouldAutoOpenInvitations = useAutomaticInvitationEntry(isSignedIn && !skipInvitation);
  const shouldRedirectToInvitations = shouldAutoOpenInvitations === true;
  const canShowForm = shouldAutoOpenInvitations === false;

  const [errorMessage, pageTitle, pageSubtitle] = useStrings(
    "xboxOnboardErrorMessage",
    "xboxOnboardPageTitle",
    "xboxOnboardPageSubtitle"
  );

  // Wait for invitation routing so CRM data loading cannot create a Hub Profile first.
  useEffect(() => {
    if (isSignedIn && shouldAutoOpenInvitations === false) {
      dispatch(xboxOnboardActions.fetchData.start());
    }
  }, [dispatch, isSignedIn, shouldAutoOpenInvitations]);

  // Redirect to /dashboard if user has already submitted — they should never come back to /new
  useEffect(() => {
    if (shouldAutoOpenInvitations === null) {
      return;
    }

    if (shouldRedirectToInvitations) {
      history.replace(`${xboxOnboardSiteLocationPrefix}/invitations`);
      return;
    }

    if (data?.isPartnerCenterAccessDenied || data?.accessDeniedCode !== undefined) {
      history.replace(getPartnerAccessDeniedPath(data?.accessDeniedCode));
      return;
    }

    if (
      data?.status &&
      data.status !== XboxOnboardApplicationStatus.FormReady &&
      data.status !== XboxOnboardApplicationStatus.Initial
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      const showWelcome =
        data.isOnboardingHubProfileMode || location.state?.skipInvitation === true;
      history.replace(
        `${xboxOnboardSiteLocationPrefix}/dashboard${
          showWelcome ? `?${partnerQueryParameters.welcome}=${enabledQueryParameterValue}` : ""
        }`
      );
    }
  }, [
    data?.accessDeniedCode,
    data?.isPartnerCenterAccessDenied,
    data?.isOnboardingHubProfileMode,
    data?.status,
    history,
    location.state?.skipInvitation,
    shouldAutoOpenInvitations,
    shouldRedirectToInvitations,
  ]);

  const isFormReady = canShowForm && data?.status === XboxOnboardApplicationStatus.FormReady;

  // Entry point of the signup funnel. Gated on the form actually rendering so
  // it is not counted for partners who are immediately redirected away.
  useEffect(() => {
    if (isFormReady) {
      trackClarityEvent(ClarityEvents.onboardFormViewed);
    }
  }, [isFormReady]);

  if (
    !isSignedIn ||
    shouldAutoOpenInvitations === null ||
    shouldRedirectToInvitations ||
    (isLoading && !data) ||
    isSubmitting
  ) {
    return (
      <PartnerPageLayout>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }

  return (
    <>
      <PartnerPageLayout>
        <h1 className={styles.pageTitle}>{pageTitle}</h1>
        <p className={styles.pageSubtitle}>{pageSubtitle}</p>

        {error && <XboxOnboardStatus message={`${errorMessage}: ${error}`} variant="error" />}

        {isFormReady && data && <XboxOnboardForm data={data} isSubmitting={isSubmitting} />}
      </PartnerPageLayout>
    </>
  );
};

export default XboxOnboardPage;
